// services/google-auth.ts — Platik-IA
// Adapter cliente de la Edge Function google-token.
// Guarda el provider_refresh_token de Google (cifrado en el servidor) y obtiene
// access_tokens frescos on-demand, para que el sync a Sheets funcione aunque el
// provider_token de la sesión de Supabase haya caducado (caduca a la hora y
// Supabase no lo refresca).

import { supabase } from './supabase';
import { env }      from '../core/env';

const FN_URL = `${env.VITE_SUPABASE_URL}/functions/v1/google-token`;

/** Error con bandera para que el llamador sepa que hay que re-autenticar con Google. */
export class GoogleReauthError extends Error {
  readonly needsReauth = true;
  constructor(message = 'Se requiere volver a iniciar sesión con Google') {
    super(message);
    this.name = 'GoogleReauthError';
  }
}

async function callFn(body: unknown): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No hay sesión activa');

  return fetch(FN_URL, {
    method:  'POST',
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type':  'application/json',
    },
    body: JSON.stringify(body),
  });
}

/** Cifra y guarda el refresh token de Google. Llamar apenas se obtiene en el login. */
export async function storeGoogleRefreshToken(refreshToken: string): Promise<void> {
  const res = await callFn({ action: 'store', refresh_token: refreshToken });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `google-token store ${res.status}`);
  }
}

/** Devuelve un access_token fresco de Google y sus segundos de vida.
 *  Lanza {@link GoogleReauthError} si no hay refresh token válido guardado. */
export async function fetchFreshAccessToken(): Promise<{ accessToken: string; expiresIn: number }> {
  const res = await callFn({ action: 'get' });

  if (res.status === 409) {
    throw new GoogleReauthError();
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? `google-token get ${res.status}`);
  }

  const data = await res.json();
  return { accessToken: data.access_token, expiresIn: data.expires_in ?? 3600 };
}
